// Returns the name of the parent menu entry whose child matches the active route,
// so that parent's dropdown starts open. menu: array of entries; some have children
// [{ name, href }]. pathname: full location pathname (e.g. "/dashboard/inventory").
export const initialOpenParent = (menu = [], pathname = '') => {
  const segment = pathname.split('dashboard/')[1] || '';
  for (const item of menu) {
    if (!item.children) continue;
    if (item.children.some((c) => c.href && segment.startsWith(c.href))) {
      return item.name;
    }
  }
  return null;
};

// Which dropdown is open after the sidebar is (re)built. A new page opens the
// parent of that page, as it always did. A rebuild on the same page — the
// Connect status or the referral flag arriving after the first build — keeps
// the dropdown the user opened, as long as it is still in the sidebar: QA
// (2026-09-25) caught Finance snapping shut when the status landed 80 ms after
// the click. With nothing open, a rebuild still opens the route's parent, in
// case that parent only appeared with the rebuild.
export const nextOpenMenu = ({ open, sidebar = [], pathname = '', routeChanged }) => {
  if (
    !routeChanged &&
    open &&
    sidebar.some((item) => item.name === open && item.children?.length)
  ) {
    return open;
  }
  return initialOpenParent(sidebar, pathname);
};

// A menu entry is visible if the user has a matching permission OR a matching
// role. Each entry lists both fields, so honouring either lets role-based
// access (e.g. Accountant=550) work without requiring every permission to be
// pre-assigned through the Permissions UI. An entry that also names a `feature`
// needs that flag to be true as well; an unresolved flag keeps the entry hidden
// (fail-closed), which is what keeps Settings -> Payments out of the sidebar of
// a company with no Connect account.
export const isNavEntryVisible = (entry, context = {}) => {
  const { permissions, roles, features } = context;
  if (entry?.feature && features?.[entry.feature] !== true) return false;
  return Boolean(
    permissions?.some((p) => entry?.permissions?.includes(p)) ||
      roles?.some((r) => entry?.roles?.includes(r)),
  );
};

export const parseStoredCompany = (raw) => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed?.id ? parsed : null;
  } catch {
    return null;
  }
};

export const pickAssignedCompany = (list = [], stored) => {
  if (!Array.isArray(list) || list.length === 0) return null;
  if (stored?.id) {
    const match = list.find((c) => c.id === stored.id);
    if (match) return match;
  }
  return list[0];
};

// Keeps a parent only while at least one of its children survives the gate.
export const filterSidebar = (menu = [], context = {}) =>
  menu
    .map((item) => {
      if (item.children) {
        const children = item.children.filter((child) =>
          isNavEntryVisible(child, context),
        );
        return children.length
          ? { ...item, children, hidden: false }
          : { ...item, hidden: true };
      }
      return isNavEntryVisible(item, context)
        ? { ...item, hidden: false }
        : { ...item, hidden: true };
    })
    .filter((item) => !item.hidden);

// Which sub-menu entry is selected for the current route: an exact match,
// else the longest child href that is a path prefix of the route (Finance ›
// Payouts › one payout; Settings › Release Notes › the editor). The section
// root is never a prefix candidate: 'finance' would light Overview up on
// every Finance page.
export const activeSubItemHref = (item, current) => {
  const children = item?.children || [];
  const exact = children.find((child) => child.href === current);
  if (exact) return exact.href;
  const deeper = children
    .filter((child) => child.href !== item?.href && String(current).startsWith(`${child.href}/`))
    .sort((a, b) => b.href.length - a.href.length)[0];
  return deeper ? deeper.href : null;
};

// A childless top-level entry (Orders, What's new, …) stays highlighted on its
// own sub-pages: `current` is the path after 'dashboard/' and is seeded once
// from the URL, so a permalink such as release-notes/<slug> opened cold used
// to light nothing. Exact match, or `href + '/'` as a prefix; the root entry
// never prefix-matches (every path would).
export const isTopLevelActive = (current, href) => {
  if (!href) return false;
  const path = String(current ?? '');
  if (path === href) return true;
  if (href === '/dashboard') return false;
  return path.startsWith(`${href}/`);
};
